import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * HC - Arc'teryx-style (owner, 2026-10-01): the photos sit in a sideways
 * slider (`scroller`): full height at their own shape on desktop with the next
 * one peeking in, one whole photo per swipe on phones. A "1 / 7" counter, round
 * arrows (desktop), arrow keys and the mouse wheel move it; a click on desktop
 * magnifies the photo under the mouse and the magnified view follows the
 * mouse. Phones pinch or double-tap instead (drag-zoom-wrapper.js). The active
 * photo is worked out from scrollLeft and each slide's offsetLeft.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [scroller] - The sideways slider holding the media.
 * @property {HTMLElement} [counter] - The current photo number.
 * @property {HTMLElement} [counterStatus] - Screen reader position announcement.
 * @property {HTMLButtonElement} [previous] - The previous arrow.
 * @property {HTMLButtonElement} [next] - The next arrow.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {number} */
  #activeIndex = 0;

  /** @type {number} */
  #wheelDelta = 0;

  /** @type {number} */
  #wheelLockUntil = 0;

  /** @type {number} */
  #magnifyFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    const { scroller } = this.refs;
    if (scroller) {
      scroller.addEventListener('scroll', this.handleScroll, { passive: true });
      scroller.addEventListener('wheel', this.#handleWheel, { passive: false });
      scroller.addEventListener('mousemove', this.#handleMouseMove);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    const { scroller } = this.refs;
    if (scroller) {
      scroller.removeEventListener('scroll', this.handleScroll);
      scroller.removeEventListener('wheel', this.#handleWheel);
      scroller.removeEventListener('mousemove', this.#handleMouseMove);
    }
    cancelAnimationFrame(this.#magnifyFrame);
  }

  /**
   * HC - the photo the slider is on: the slide showing the most of itself,
   * the leftmost on a tie, and the last one once the slider reaches its end.
   * @returns {number}
   */
  #computeActiveIndex() {
    const { scroller, media } = this.refs;
    if (!scroller || !media?.length) return this.#activeIndex;

    const left = scroller.scrollLeft;
    const width = scroller.clientWidth;
    if (!width) return this.#activeIndex;

    const maxScroll = scroller.scrollWidth - width;
    if (maxScroll > 2 && left >= maxScroll - 2) return media.length - 1;

    let best = 0;
    let bestVisible = -1;
    media.forEach((slide, index) => {
      const start = slide.offsetLeft;
      const visible = Math.min(start + slide.offsetWidth, left + width) - Math.max(start, left);
      if (visible > bestVisible + 1) {
        best = index;
        bestVisible = visible;
      }
    });
    return best;
  }

  /**
   * HC - moves the slider so photo `index` sits at its start.
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  goTo(index, behavior) {
    const { scroller, media } = this.refs;
    if (!scroller || !media?.length) return;

    const target = Math.max(0, Math.min(media.length - 1, index));
    const slide = media[target];
    if (!slide) return;

    scroller.scrollTo({ left: slide.offsetLeft, behavior: behavior ?? (prefersReducedMotion() ? 'instant' : 'smooth') });
    this.#setActive(target);
  }

  /** HC - next photo (arrow button, arrow key, mouse wheel). */
  next() {
    this.goTo(this.#activeIndex + 1);
  }

  /** HC - previous photo (arrow button, arrow key, mouse wheel). */
  previous() {
    this.goTo(this.#activeIndex - 1);
  }

  /**
   * HC - counter, arrows and thumbnails follow the active photo.
   * @param {number} index
   */
  #setActive(index) {
    const changed = index !== this.#activeIndex;
    this.#activeIndex = index;

    const { media, thumbnails, counter, counterStatus, previous, next, scroller } = this.refs;
    if (counter) counter.textContent = String(index + 1);
    if (counterStatus && changed) {
      counterStatus.textContent = (counterStatus.dataset.status || '').replace('__i__', String(index + 1));
    }

    const atEnd = !!scroller && scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 2;
    if (previous) previous.disabled = index <= 0;
    if (next) next.disabled = index >= media.length - 1 || (atEnd && index > 0);

    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    buttons.forEach((button, i) => button.setAttribute('aria-current', `${i === index}`));
    const thumbnail = buttons[index];
    if (changed && thumbnail) {
      scrollIntoView(thumbnail, { ancestor: thumbnails, behavior: 'instant', block: 'center', inline: 'center' });
    }

    if (changed) this.#unmagnify();
  }

  /**
   * HC - desktop: click a photo to magnify it (2x, centred on the click), click
   * again to let go; clicking the photo peeking in at the edge moves to it.
   * Phones pinch or double-tap instead.
   * @param {Event} [event]
   */
  toggleMagnify(event) {
    if (isMobileBreakpoint()) return;

    const slide =
      event?.target instanceof Element ? /** @type {HTMLElement | null} */ (event.target.closest('.product-media-container')) : null;
    if (!slide) return;

    if (slide.classList.contains('is-magnified')) {
      this.#unmagnify();
      return;
    }

    const { scroller, media } = this.refs;
    const index = media.indexOf(slide);
    if (scroller && index !== -1) {
      const start = slide.offsetLeft;
      const fullyShown =
        start >= scroller.scrollLeft - 1 && start + slide.offsetWidth <= scroller.scrollLeft + scroller.clientWidth + 1;
      if (!fullyShown) {
        this.goTo(index);
        return;
      }
    }

    this.#unmagnify();
    if (event instanceof MouseEvent) this.#setMagnifyOrigin(slide, event);
    slide.classList.add('is-magnified');
    this.loadHighResolutionImage(slide);
  }

  /**
   * @param {HTMLElement} slide
   * @param {MouseEvent} event
   */
  #setMagnifyOrigin(slide, event) {
    const rect = slide.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    slide.style.setProperty('--hc-magnify-x', `${x}%`);
    slide.style.setProperty('--hc-magnify-y', `${y}%`);
  }

  #unmagnify() {
    const { scroller } = this.refs;
    if (!scroller) return;
    for (const slide of scroller.querySelectorAll('.is-magnified')) slide.classList.remove('is-magnified');
  }

  /** @param {MouseEvent} event */
  #handleMouseMove = (event) => {
    const slide = this.refs.scroller?.querySelector('.is-magnified');
    if (!(slide instanceof HTMLElement) || this.#magnifyFrame) return;
    this.#magnifyFrame = requestAnimationFrame(() => {
      this.#magnifyFrame = 0;
      this.#setMagnifyOrigin(slide, event);
    });
  };

  /**
   * HC - an up/down mouse wheel steps the slider one photo per flick;
   * sideways trackpad swipes scroll it natively.
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (isMobileBreakpoint()) return;
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    event.preventDefault();
    const now = performance.now();
    if (now < this.#wheelLockUntil) return;

    this.#wheelDelta += event.deltaY;
    if (Math.abs(this.#wheelDelta) < 30) return;

    if (this.#wheelDelta > 0) this.next();
    else this.previous();
    this.#wheelDelta = 0;
    this.#wheelLockUntil = now + 600;
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      this.#unmagnify();

      if (targetThumbnail instanceof HTMLElement) {
        scrollIntoView(targetThumbnail, { ancestor: thumbnails, behavior: 'instant', block: 'center', inline: 'center' });
      }
      // HC - slider position by offsetLeft: scrollIntoView aligns a slide
      // past the right edge to the END of the slider, not its start.
      this.goTo(index, 'instant');
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(() => {
    const activeIndex = this.#computeActiveIndex();
    const activeMedia = this.refs.media[activeIndex];
    if (!activeMedia) return;

    this.#setActive(activeIndex);
    this.loadHighResolutionImage(activeMedia);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    this.#unmagnify();
    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // HC - the slider's active photo (scroll position), not an observer pass
    const activeIndex = this.#computeActiveIndex();
    const mostVisibleElement = media[activeIndex];
    if (!mostVisibleElement) return this.closeDialog();
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#unmagnify();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    // HC - left/right arrow keys move the slider
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      if (event.key === 'ArrowRight') this.next();
      else this.previous();
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-current', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      this.goTo(index, options.behavior);
      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
